// A search engine, social network, browser, operating system, AI assistant or well-known referring site is coloured as ITSELF: its tooltip, its sidebar dot, its bubble and the banner of the drawer it opens all take its brand colour, the one
// its icon wears (Chris, 2026-10-17: a teal Bing icon sat under a red tooltip).
//
// Every colour in the app travels as a palette INDEX (setColor(index)), so brand colours are a second palette whose indexes start at BRAND_COLOUR_BASE. They are NOT appended
// to the main palette: the main palette's length is the modulus of the name hash, and growing it would re-colour every category on every filter.
//
// Each value is the brand's own colour, deepened only where the pure brand colour is too light for the white text on a tooltip (a test holds every entry to a contrast floor).
// Brands whose own colour is a light yellow (AOL, Snapchat) are left out on purpose: the only way to make white text readable on it is an olive nobody would call the brand,
// and the palette already banned olive. They keep their ordinary palette colour.
export const BRAND_COLOUR_BASE = 1000;

const BRANDS: Record<string, Record<string, string>> = {
  search_engine: {
    google: "#4285F4",
    bing: "#008373",
    duckduckgo: "#DE5833",
    yahoo: "#6001D2",
    brave: "#E5481F",
    ecosia: "#3A7D2C",
    startpage: "#6A3FE8",
    qwant: "#2D3A8C",
    baidu: "#2932E1",
    yandex: "#D6311B",
    mojeek: "#5E8F2A",
    naver: "#039C47",
    ask: "#D93B2B",
    seznam: "#CC0000",
    sogou: "#E4511A",
    dogpile: "#D4312F",
    lycos: "#2B3AD6",
    swisscows: "#D5211D",
  },
  social_network: {
    facebook: "#1877F2",
    x: "#0F1419",
    linkedin: "#0A66C2",
    reddit: "#E63E00",
    instagram: "#C13584",
    pinterest: "#E60023",
    tiktok: "#161823",
    threads: "#101010",
    youtube: "#FF0000",
    rumble: "#548F1C",
    substack: "#E65A10",
    whatsapp: "#12A150",
    telegram: "#1C93D1",
    discord: "#5865F2",
    tumblr: "#36465D",
    mastodon: "#6364FF",
    bluesky: "#0A7AEB",
    quora: "#B92B27",
    medium: "#242424",
    twitch: "#9146FF",
    vk: "#0A6FE0",
    weibo: "#E6162D",
    wechat: "#05A050",
    line: "#05A24B",
    truthsocial: "#5448EE",
    gab: "#109A55",
    flickr: "#0063DC",
    nextdoor: "#5C9400",
  },
};

// Browsers and operating systems: each is the colour of the bundled icon it is drawn with (read from the SVG files, src/assets/images/visitor_analytics_icons), deepened only
// where white text would not be readable (Android's #A4C639 and Windows' #00ADEF).
BRANDS.browser = {
  chrome: "#DB4437",
  edge: "#0078D7",
  firefox: "#E05A27",
  ie: "#1A8FC9",
  opera: "#D6162A",
  safari: "#2F6FE8",
  "samsung internet": "#3E14D8",
  netscape: "#007C85",
};
BRANDS.os = {
  android: "#2E9E5B",
  ios: "#1C1C1E",
  ipados: "#004562",
  macos: "#1A7BB3",
  macintosh: "#1A7BB3",
  windows: "#0A8CC8",
  "windows phone": "#004562",
  windowsxp: "#2E499E",
  linux: "#2B2B2B",
  blackberry: "#004562",
  kindle: "#004562",
  palm: "#004562",
  playbook: "#004562",
};

// "Nothing to report" (the no-value bucket and the literal Unknown): one fixed slate grey, the colour of the plain unknown icon, so the group reads as "not there" in every place it
// appears - dot, bubble, tooltip, drawer banner - instead of taking a random palette colour (Chris, 2026-10-17: it came out purple).
BRANDS.none = { "no-data": "#8A93A0" };

// Direct visits: the dark blue of the Direct icon (direct.svg), in the Traffic Source filter and for the Referral Link filter's "/" bucket (a visit with no referrer), so the icon,
// the bubble, the tooltip and the drawer banner agree (Chris, 2026-10-17: the icon was dark blue and the tooltip and banner green).
BRANDS.traffic_source = { direct: "#004562" };

// Referring sites. A referrer is a place on the web, so the sites that ARE a known brand wear that brand's colour (REFERRER_OF points at the same entries as the filters
// above - one colour per brand, never two), and AI assistants and a few big sites have their own here. Any other site keeps its ordinary palette colour: its icon comes
// from a favicon service that sends no CORS headers (measured 2026-10-17), so a page cannot read an unknown icon's colour.
BRANDS.referrer = {
  "chatgpt.com": "#10A37F",
  "chat.openai.com": "#10A37F",
  "perplexity.ai": "#1A9FB0",
  "claude.ai": "#C4623F",
  "gemini.google.com": "#6F5BD6",
  "copilot.microsoft.com": "#0F6CBD",
  "meta.ai": "#0866FF",
  "grok.com": "#1C1C1C",
  "poe.com": "#5D5CDE",
  "github.com": "#24292F",
  "news.ycombinator.com": "#E65C00",
  "stackoverflow.com": "#E0701A",
  "wikipedia.org": "#3366CC",
  "producthunt.com": "#DA552F",
  "bbc.com": "#BB1919",
  "bbc.co.uk": "#BB1919",
};
// domain -> [filter, value] of the brand it belongs to
const REFERRER_OF: Record<string, [string, string]> = {
  "google.com": ["search_engine", "google"], "bing.com": ["search_engine", "bing"], "duckduckgo.com": ["search_engine", "duckduckgo"], "yahoo.com": ["search_engine", "yahoo"],
  "search.brave.com": ["search_engine", "brave"], "ecosia.org": ["search_engine", "ecosia"], "startpage.com": ["search_engine", "startpage"], "qwant.com": ["search_engine", "qwant"],
  "baidu.com": ["search_engine", "baidu"], "yandex.com": ["search_engine", "yandex"], "yandex.ru": ["search_engine", "yandex"], "mojeek.com": ["search_engine", "mojeek"],
  "naver.com": ["search_engine", "naver"], "ask.com": ["search_engine", "ask"], "seznam.cz": ["search_engine", "seznam"], "sogou.com": ["search_engine", "sogou"],
  "dogpile.com": ["search_engine", "dogpile"], "lycos.com": ["search_engine", "lycos"], "swisscows.com": ["search_engine", "swisscows"],
  "facebook.com": ["social_network", "facebook"], "fb.com": ["social_network", "facebook"], "fb.me": ["social_network", "facebook"], "messenger.com": ["social_network", "facebook"],
  "x.com": ["social_network", "x"], "twitter.com": ["social_network", "x"], "t.co": ["social_network", "x"], "linkedin.com": ["social_network", "linkedin"],
  "lnkd.in": ["social_network", "linkedin"], "reddit.com": ["social_network", "reddit"], "instagram.com": ["social_network", "instagram"], "pinterest.com": ["social_network", "pinterest"],
  "tiktok.com": ["social_network", "tiktok"], "threads.net": ["social_network", "threads"], "youtube.com": ["social_network", "youtube"], "youtu.be": ["social_network", "youtube"],
  "rumble.com": ["social_network", "rumble"], "substack.com": ["social_network", "substack"], "whatsapp.com": ["social_network", "whatsapp"], "wa.me": ["social_network", "whatsapp"],
  "telegram.org": ["social_network", "telegram"], "t.me": ["social_network", "telegram"], "discord.com": ["social_network", "discord"], "discord.gg": ["social_network", "discord"],
  "tumblr.com": ["social_network", "tumblr"], "mastodon.social": ["social_network", "mastodon"], "bsky.app": ["social_network", "bluesky"], "quora.com": ["social_network", "quora"],
  "medium.com": ["social_network", "medium"], "twitch.tv": ["social_network", "twitch"], "vk.com": ["social_network", "vk"], "weibo.com": ["social_network", "weibo"],
  "wechat.com": ["social_network", "wechat"], "line.me": ["social_network", "line"], "truthsocial.com": ["social_network", "truthsocial"], "gab.com": ["social_network", "gab"],
  "flickr.com": ["social_network", "flickr"], "nextdoor.com": ["social_network", "nextdoor"],
};

const FLAT: Array<{ key: string; hex: string }> = [];
const INDEX: Record<string, number> = {};
Object.keys(BRANDS).forEach((menu) => {
  Object.keys(BRANDS[menu]).forEach((value) => {
    INDEX[`${menu}:${value}`] = BRAND_COLOUR_BASE + FLAT.length;
    FLAT.push({ key: `${menu}:${value}`, hex: BRANDS[menu][value] });
  });
});

/** The palette index of the "not there" grey. */
export const NO_DATA_COLOUR_INDEX: number = INDEX["none:no-data"];

/** The palette index of a brand's colour, or undefined when this value has none (every other filter, and brands left out on purpose). */
export const brandColourIndex = (menuType: string | undefined, value: string): number | undefined => {
  if (!menuType || typeof value !== "string") {
    return undefined;
  }
  if (menuType !== "referrer") {
    return INDEX[`${menuType}:${value.toLowerCase()}`];
  }
  if (value === "/") {
    return INDEX["traffic_source:direct"];
  }
  // a referrer value is a URL or a bare domain: take the host, then try it and each parent domain (www.facebook.com -> facebook.com)
  const host = value.toLowerCase().replace(/^[a-z]+:\/\//, "").split(/[/?#:]/)[0];
  const labels = host.split(".");
  for (let i = 0; i < labels.length - 1; i++) {
    const domain = labels.slice(i).join(".");
    if (INDEX[`referrer:${domain}`] !== undefined) {
      return INDEX[`referrer:${domain}`];
    }
    const brand = REFERRER_OF[domain];
    if (brand) {
      return INDEX[`${brand[0]}:${brand[1]}`];
    }
  }

  return undefined;
};

/** The colour for an index returned by brandColourIndex, or undefined for an index that is not a brand's. */
export const brandColourHex = (index: number): string | undefined => (index >= BRAND_COLOUR_BASE ? FLAT[index - BRAND_COLOUR_BASE]?.hex : undefined);

export const ALL_BRAND_COLOURS = (): Array<{ key: string; hex: string }> => FLAT.slice();
